// Check if Router and AuthProvider imports are unused
import React from 'react';
// Restore imports from react-router-dom, add useLocation
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
// Keep AuthProvider and useAuth for context
import { AuthProvider, useAuth } from './contexts/AuthContext';
// Keep ToastContainer imports if used (it is used at the bottom)
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { SettingsProvider } from './contexts/SettingsContext';

// Layout components
import Layout from './components/layout/Layout';
import AuthLayout from './components/layout/AuthLayout';
import PrivateRoute from './components/common/PrivateRoute';

// Auth pages
import Login from './pages/auth/Login';

// Dashboard
import Dashboard from './pages/dashboard/Dashboard';

// Structures
import StructureList from './pages/structures/StructureList';
import StructureForm from './pages/structures/StructureForm';
import StructureDetails from './pages/structures/StructureDetails';
import PriceList from './pages/structures/PriceList';
import PriceListPrintPage from './pages/structures/PriceListPrintPage'; // Import the new print page
import StructureAvailability from './pages/structures/StructureAvailability';
import StructureAvailabilityPrintPage from './pages/structures/StructureAvailabilityPrintPage'; // Added for print view
import StructureOccupancyCalendar from './pages/structures/StructureOccupancyCalendar';
import NewPriceListPage from './pages/structures/NewPriceListPage'; // Import for new price list
import NewPriceListPrintPage from './pages/structures/NewPriceListPrintPage'; // Import for new price list print page

// Clients
import ClientList from './pages/clients/ClientList';
import ClientForm from './pages/clients/ClientForm';
import ClientDetails from './pages/clients/ClientDetails';

// Files
import ClientFilesList from './pages/files/ClientFilesList';
import ClientFileDetails from './pages/files/ClientFileDetails';

// Contacts (New Import)
import ContactList from './pages/contacts/ContactList';
import ContactForm from './pages/contacts/ContactForm';

// Quotes
import QuoteList from './pages/quotes/QuoteList';
import QuoteForm from './pages/quotes/QuoteForm';
import QuoteDetails from './pages/quotes/QuoteDetails';
import QuotePrintPage from './pages/quotes/QuotePrintPage'; // Import the new print page

// Orders
import OrderList from './pages/orders/OrderList';
import OrderForm from './pages/orders/OrderForm';
import OrderDetails from './pages/orders/OrderDetails';
import OrderPrintPage from './pages/orders/OrderPrintPage'; // Import the new print page
import OrderAppendixPrintPage from './pages/orders/OrderAppendixPrintPage'; // Import the appendix print page

// Work Orders
import WorkOrderList from './pages/workOrders/WorkOrderList';
import WorkOrderForm from './pages/workOrders/WorkOrderForm';
import WorkOrderDetails from './pages/workOrders/WorkOrderDetails';

// Contracts
import ContractList from './pages/contracts/ContractList';
import ContractForm from './pages/contracts/ContractForm';
import ContractDetails from './pages/contracts/ContractDetails';
import ContractAppendixForm from './pages/contracts/ContractAppendixForm';
import ContractPrintPage from './pages/contracts/ContractPrintPage'; // Import the contract print page
import ContractAppendixPrintPage from './pages/contracts/ContractAppendixPrintPage'; // Import the appendix print page

// Payments
import PaymentList from './pages/payments/PaymentList';
import PaymentForm from './pages/payments/PaymentForm';

// Expenses
import ExpenseList from './pages/expenses/ExpenseList';
import ExpenseForm from './pages/expenses/ExpenseForm';

// Reports
import ReportDashboard from './pages/reports/ReportDashboard';
import FinancialReport from './pages/reports/FinancialReport';
import StructureReport from './pages/reports/StructureReport';
import ClientReport from './pages/reports/ClientReport';
import ClientReportPrintPage from './pages/reports/ClientReportPrintPage'; // Import the client print page
import OrderReport from './pages/reports/OrderReport';
import OrderReportPrintPage from './pages/reports/OrderReportPrintPage'; // Import the order print page
import WorkOrderReport from './pages/reports/WorkOrderReport';
import WorkOrderReportPrintPage from './pages/reports/WorkOrderReportPrintPage'; // Import the work order print page
import ExpenseReport from './pages/reports/ExpenseReport';
import ExpenseReportPrintPage from './pages/reports/ExpenseReportPrintPage'; // Import the expense print page
import AnalyticsReport from './pages/reports/AnalyticsReport';
import FinancialReportPrintPage from './pages/reports/FinancialReportPrintPage'; // Import the financial print page
import StructureReportPrintPage from './pages/reports/StructureReportPrintPage'; // Import the structure print page

// Other settings
import UserManagement from './pages/settings/UserManagement';
import Settings from './pages/settings/Settings';
import RolePermissions from './pages/settings/RolePermissions';
import AuditLogView from './pages/settings/AuditLogView'; // Import AuditLogView

// Import WhatsApp Page
import WhatsAppPage from './pages/whatsapp/WhatsAppPage'; // Added import
import EmailPage from './pages/email/EmailPage'; // Added import

function AppContent() {
  // Restore useAuth hook usage
  const { user, loading } = useAuth();
  // Get location
  const location = useLocation();
  const showLayout = !!user && !loading;
  // Determine if padding should be applied
  const applyPadding = showLayout && location.pathname !== '/whatsapp-standalone';

  if (loading) {
    return <div className="flex justify-center items-center h-dvh"><div className="text-xl">Система управления рекламным агентством</div></div>;
  }

  return (
    <div className="flex h-dvh bg-gray-100">
      <div className="flex-1 flex flex-col overflow-auto">
        {/* Apply padding conditionally */}
        <main className={`flex-1 overflow-x-auto overflow-y-auto ${applyPadding ? 'bg-gray-100 p-6' : 'bg-gray-100'}`}>
          <Routes>
            {/* Public routes */}
            <Route element={<AuthLayout />}>
              <Route path="/login" element={<Login />} />
              <Route path="/structures/public-price-list" element={<PriceList isPublic={true} />} />
            </Route>

            {/* Print Routes (Outside Main Layout but potentially protected) */}
            {/* If user is not logged in, AppContent redirects to /login */}
            {user && (
              <> {/* Use fragment to group multiple routes */}
                <Route path="/quotes/:id/print" element={<QuotePrintPage />} />
                <Route path="/contracts/:id/print" element={<ContractPrintPage />} />
                {/* Route uses contract ID based on current backend setup */}
                <Route path="/contracts/:id/appendix/print" element={<ContractAppendixPrintPage />} />
                <Route path="/reports/financial/print" element={<FinancialReportPrintPage />} />
                <Route path="/reports/structures/print" element={<StructureReportPrintPage />} />
                <Route path="/structures/price-list/print" element={<PriceListPrintPage />} />
                <Route path="/reports/clients/print" element={<ClientReportPrintPage />} />
                <Route path="/reports/orders/print" element={<OrderReportPrintPage />} />
                <Route path="/reports/workorders/print" element={<WorkOrderReportPrintPage />} />
                <Route path="/reports/expenses/print" element={<ExpenseReportPrintPage />} />
                <Route path="/orders/:id/print" element={<OrderPrintPage />} />
                <Route path="/orders/:id/appendix/print" element={<OrderAppendixPrintPage />} /> {/* New route for appendix */}
                <Route path="/structures/availability/print" element={<StructureAvailabilityPrintPage />} /> {/* Printable availability */}
                <Route path="/structures/new-price-list/print" element={<NewPriceListPrintPage />} /> {/* New Price List Print Page */}

                {/* Standalone WhatsApp Page */}
                <Route path="/whatsapp-standalone" element={<WhatsAppPage />} />
                {/* Add other print routes here as needed */}
              </>
            )}

            {/* Protected routes */}
            {/* Ensure Layout is correctly wrapping protected routes */}
            <Route element={showLayout ? <PrivateRoute><Layout /></PrivateRoute> : <Navigate to="/login" />}>
              <Route path="/" element={<Dashboard />} />

              {/* Structures */}
              <Route path="/structures" element={<StructureList />} />
              <Route path="/structures/new" element={<StructureForm />} />
              <Route path="/structures/edit/:id" element={<StructureForm />} />
              <Route path="/structures/:id" element={<StructureDetails />} />
              <Route path="/structures/availability" element={<StructureAvailability />} />
              <Route path="/structures/occupancy-calendar" element={<StructureOccupancyCalendar />} />
              <Route path="/structures/price-list" element={<PriceList isPublic={false} />} />
              <Route path="/structures/new-price-list" element={<NewPriceListPage />} />

              {/* Clients */}
              <Route path="/clients" element={<ClientList />} />
              <Route path="/clients/new" element={<ClientForm />} />
              <Route path="/clients/edit/:id" element={<ClientForm />} />
              <Route path="/clients/:id" element={<ClientDetails />} />

              {/* Files */}
              <Route path="/files" element={<ClientFilesList />} />
              <Route path="/files/clients/:clientId" element={<ClientFileDetails />} />

              {/* Contacts (New Routes) */}
              <Route path="/contacts" element={<ContactList />} />
              <Route path="/contacts/new" element={<ContactForm />} />
              <Route path="/contacts/edit/:id" element={<ContactForm />} />
              {/* Optional: Add a ContactDetails route if needed later */}
              {/* <Route path="/contacts/:id" element={<ContactDetails />} /> */}

              {/* Quotes */}
              <Route path="/quotes" element={<QuoteList />} />
              <Route path="/quotes/new" element={<QuoteForm />} />
              <Route path="/quotes/edit/:id" element={<QuoteForm />} />
              <Route path="/quotes/:id" element={<QuoteDetails />} />

              {/* Orders */}
              <Route path="/orders" element={<OrderList />} />
              <Route path="/orders/new" element={<OrderForm />} />
              <Route path="/orders/edit/:id" element={<OrderForm />} />
              <Route path="/orders/:id" element={<OrderDetails />} />

              {/* Work Orders */}
              <Route path="/work-orders" element={<WorkOrderList />} />
              <Route path="/work-orders/new" element={<WorkOrderForm />} />
              <Route path="/work-orders/edit/:id" element={<WorkOrderForm />} />
              <Route path="/work-orders/:id" element={<WorkOrderDetails />} />

              {/* Contracts */}
              <Route path="/contracts" element={<ContractList />} />
              <Route path="/contracts/new" element={<ContractForm />} />
              <Route path="/contracts/edit/:id" element={<ContractForm />} />
              <Route path="/contracts/:id" element={<ContractDetails />} />
              <Route path="/contracts/:id/appendix/new" element={<ContractAppendixForm />} />
              <Route path="/contracts/appendix/edit/:id" element={<ContractAppendixForm />} />

              {/* Payments */}
              <Route path="/payments" element={<PaymentList />} />
              <Route path="/payments/new" element={<PaymentForm />} />
              <Route path="/payments/edit/:id" element={<PaymentForm />} />

              {/* Expenses */}
              <Route path="/expenses" element={<ExpenseList />} />
              <Route path="/expenses/new" element={<ExpenseForm />} />
              <Route path="/expenses/edit/:id" element={<ExpenseForm />} />

              {/* Reports */}
              <Route path="/reports" element={<ReportDashboard />} />
              <Route path="/reports/financial" element={<FinancialReport />} />
              <Route path="/reports/structures" element={<StructureReport />} />
              <Route path="/reports/clients" element={<ClientReport />} />
              <Route path="/reports/orders" element={<OrderReport />} />
              <Route path="/reports/workorders" element={<WorkOrderReport />} />
              <Route path="/reports/expenses" element={<ExpenseReport />} />
              <Route path="/reports/analytics" element={<AnalyticsReport />} />

              {/* Settings */}
              <Route path="/settings/general" element={<Settings />} />
              <Route path="/settings/users" element={<UserManagement />} />
              <Route path="/settings/permissions" element={<RolePermissions />} />
              <Route path="/settings/audit-log" element={<AuditLogView />} />

              {/* WhatsApp Chat */}
              <Route path="/whatsapp" element={<WhatsAppPage />} />
              
              {/* Email Page */}
              <Route path="/email-standalone" element={<EmailPage />} />
            </Route>

            {/* Fallback - redirect based on auth status */}
            <Route path="*" element={<Navigate to={user ? "/" : "/login"} state={{ from: location }} />} />
          </Routes>
        </main>
      </div>
      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <SettingsProvider>
        <AppContent />
      </SettingsProvider>
    </AuthProvider>
  );
}

export default App;
